import { EntityStringId } from 'app/types/entity';
import { IdentityEntityStringIdCodec } from 'app/types/entity/codecs';
import { decodeOrThrow } from 'app/utils/ioTs';
import { fold } from 'fp-ts/lib/Either';
import { pipe } from 'fp-ts/lib/pipeable';
import * as iots from 'io-ts';

enum TopLevelFields {
  Data = 'data',
  Meta = 'meta',
}

enum MetaFields {
  Search = 'search',
  Supplementary = 'supplementary',
}

enum ReservedEntityFields {
  Id = 'id',
}

const DataDecoder = <T, R>(t: iots.Type<T, R>): iots.Decoder<unknown, { [TopLevelFields.Data]: T }> => {
  return iots.interface({ [TopLevelFields.Data]: t }).asDecoder();
};

const ResponseIdDecoder = DataDecoder(
  iots.interface({
    [ReservedEntityFields.Id]: IdentityEntityStringIdCodec,
  }),
);

export const getResponseId = (payload: unknown): EntityStringId => {
  const {
    [TopLevelFields.Data]: { [ReservedEntityFields.Id]: id },
  } = decodeOrThrow(ResponseIdDecoder, payload);
  return id;
};

const ResponseIdsDecoder = DataDecoder(
  iots.array(
    iots.interface({
      [ReservedEntityFields.Id]: IdentityEntityStringIdCodec,
    }),
  ),
);

/**
 * Retrieves an array of IDs from a response whose payload gives back a list of entities.
 */
export const getResponseIds = (payload: unknown): EntityStringId[] => {
  const { [TopLevelFields.Data]: entities } = decodeOrThrow(ResponseIdsDecoder, payload);
  return entities.map(({ id }) => id);
};

const MetaDecoder = <T, R>(t: iots.Type<T, R>): iots.Decoder<unknown, { [TopLevelFields.Meta]: T }> => {
  return iots.interface({ [TopLevelFields.Meta]: t }).asDecoder();
};

export interface ISearchMeta {
  totalCount: number;
}

const SearchDecoder = MetaDecoder(
  iots.interface({
    [MetaFields.Search]: iots.interface({ total_count: iots.number }),
  }),
);

/**
 * Retrieves the search meta data from a response.
 */
export const getSearchMeta = (payload: unknown): ISearchMeta => {
  const {
    [TopLevelFields.Meta]: {
      [MetaFields.Search]: { total_count },
    },
  } = decodeOrThrow(SearchDecoder, payload);
  return { totalCount: total_count };
};

const SupplementalDecoder = MetaDecoder(
  iots.interface({ [MetaFields.Supplementary]: iots.unknown }),
);

export function parseSupplementalData <A>(payload: unknown, type: iots.Decoder<unknown, A>): A | null {
  const parser = (data: unknown): A | null => pipe(
    SupplementalDecoder.decode(data),
    fold(
      (_l) => undefined,
      ({
        [TopLevelFields.Meta]: {
          [MetaFields.Supplementary]: supplementary,
        },
      }) => supplementary,
    ),
    type.decode,
    fold(
      (_l): null => null,
      (r) => r,
    ),
  );
  return parser(payload);
}
